<script setup>
import {ref, reactive, watch} from "vue";

const props = defineProps({
    strings: {
        information: Object,
        contacts: Object,
        payment: Object
    },
    product: {
        ID: Number,
        price: Number,
        price_id: String,
        coupon_id: String
    },
    url: String,
    lost_password_url: String,
    images: Object,
    user: Object
});

const Step = {
    Information: "information",
    ContactDetails: "contacts",
    Payment: "payment"
};
const stepLabels = {
    [Step.ContactDetails]: "Uppgifter",
    [Step.Information]: "Ärende",
    [Step.Payment]: "Betalning",
};

const stepNumbers = {
    [Step.ContactDetails]: 1,
    [Step.Information]: 2,
    [Step.Payment]: 3,
};
const stepList = [Step.ContactDetails, Step.Information, Step.Payment];

const activeStep = ref(Step.ContactDetails);

const data = reactive({
    city: "",
    language: "",
    orientation: "",
    text: "",
    name: "",
    surname: "",
    email: "",
    phone: "",
    product: props.product,
    password: "",
    display_name: ""
});

const checkbox = ref(false);
const isLoggedIn = ref(false);
const isRegistration = ref(false);

if (props.user) {
    data.name = props.user.name;
    data.surname = props.user.surname;
    data.email = props.user.email;
    data.phone = props.user.phone;
    data.display_name = props.user.display_name;
    isLoggedIn.value = true;
}

const validationText = reactive({
    city: "",
    language: "",
    orientation: "",
    text: "",
    email: "",
    name: "",
    password: ""
});

const buttonText = {
    next: "Spara och gå vidare",
    back: "Tillbaka"
};

const loading = ref(false);

const movingForward = ref(true);

watch(activeStep, (newValue, oldValue) => {
    movingForward.value = stepNumbers[newValue] > stepNumbers[oldValue];
});

function gotoStep(step) {
    if (activeStep.value === Step.ContactDetails && isLoggedIn.value) {
        activeStep.value = step;
    }

    if (activeStep.value === Step.ContactDetails && !isLoggedIn.value) {
        if (!validateName() || !validateEmail() || !checkbox.value) {
            return;
        }

        if (validateEmail()) {
            checkEmail(step);
            return;
        }
    }

    if (stepNumbers[step] > stepNumbers[Step.Information] && !(validateCity() && validateLanguage() && validateOrientation() && validateText())) {
        return;
    }

    activeStep.value = step;
}

function validateEmail() {
    if (!data.email) {
        validationText.email = "E-post är obligatoriskt fält.";
        return false;
    } else if (!data.email.includes("@")) {
        validationText.email = "Vänligen ange en e-postadress.";
        return false;
    }
    validationText.email = "";
    return true;
}

function validateCity() {
    if (!data.city) {
        validationText.city = "Stad är obligatoriskt fält.";
        return false;
    }
    validationText.city = "";
    return true;
}

function validateLanguage() {
    if (!data.language) {
        validationText.language = "Språk är obligatoriskt fält.";
        return false;
    }

    validationText.language = "";
    return true;
}

function validateOrientation() {
    if (!data.orientation) {
        validationText.orientation = "Inriktning är obligatoriskt fält.";
        return false;
    }

    validationText.orientation = "";
    return true;
}

function validateText() {
    if (!data.text) {
        validationText.text = "Text är obligatoriskt fält.";
        return false;
    }

    validationText.text = "";
    return true;
}

function validateName() {
    if (!data.name) {
        validationText.name = "Förnamn är obligatoriskt fält.";
        return false;
    }
    validationText.name = "";
    return true;
}

function validatePassword() {
    if (!data.password) {
        validationText.password = "Företag är obligatoriskt fält.";
        return false;
    }
    validationText.password = "";
    return true;
}

function getFormatedPrice($price) {
    return `${$price.toFixed(2).replace(".", ",")} kr`;
}

function getTaxAmount($price) {
    return getFormatedPrice($price * 0.25);
}

function checkEmail(step) {
    loading.value = true;
    const message = document.querySelector(".step .errorMessage");
    message.innerText = "";

    fetch(window.RES.base + "wa-theme/v1/check-user-email", {
        method: "POST",
        headers: {
            "Content-type": "application/json",
        },
        body: JSON.stringify({
            email: data.email,
        })
    }).then(response => response.json())
        .then(res => {
            loading.value = false;
            if (res.success) {
                message.innerText = "Denna e-postadress är redan registrerad. Välj en annan eller logga in.";
            } else {
                activeStep.value = step;
            }
        });
}

function createCheckoutSession() {
    loading.value = true;
    const message = document.querySelector(".step .errorMessage");
    message.innerText = "";

    fetch(window.RES.base + "wa-theme/v1/create-checkout-session", {
        method: "POST",
        headers: {
            "Content-type": "application/json",
        },
        body: JSON.stringify({
            url: props.url,
            data: data,
            createAccount: isRegistration,
            postType: 'lawyer'
        })
    }).then(response => response.json())
        .then(res => {
            loading.value = false;
            if (res.success) {
                window.location.href = res.url;
            } else {
                console.error(res.message);
                message.innerText = "Hoppsan! Något gick fel. Vänligen kontakta oss via e-post.";
            }
        });
}

function userLogin() {
    if (validateEmail() && validatePassword()) {
        loading.value = true;
        const message = document.querySelector(".step .errorMessage");
        message.innerHTML = "";

        fetch(window.RES.base + "wa-theme/v1/user-login", {
            method: "POST",
            headers: {
                "Content-type": "application/json",
            },
            body: JSON.stringify({
                email: data.email,
                password: data.password,
            })
        }).then(response => response.json())
            .then(res => {
                loading.value = false;
                if (res.success) {
                    data.name = res.user.name;
                    data.surname = res.user.surname;
                    data.phone = res.user.phone;
                    data.email = res.user.email;
                    data.display_name = res.user.display_name;
                    isLoggedIn.value = true;
                    gotoStep(Step.Information);
                } else {
                    message.innerHTML = res.error;
                }
            });
    }
}

function userLogout() {
    loading.value = true;

    fetch(window.RES.base + "wa-theme/v1/user-logout", {
        method: "POST",
        headers: {
            "Content-type": "application/json",
        }
    }).then(response => response.json())
        .then(res => {
            if (res.success) {
                loading.value = false;
                data.name = "";
                data.surname = "";
                data.email = "";
                data.phone = "";
                data.display_name = "";
                data.password = "";
                isLoggedIn.value = false;
            }
        });
}

</script>
<template>
    <div class="container is--small"
         :class="{'is--loading' : loading, [`direction--${movingForward ? 'right' : 'left'}`]: true}">
        <header class="header">
            <div v-for="(step) of stepList" :key="step" class="header-step"
                 @click="gotoStep(step)">
                <span class="step-number"
                      :class="{ 'is--active': stepNumbers[step] <= stepNumbers[activeStep] }">
                    {{ stepNumbers[step] }}
                </span>
                <span class="step-name">{{ stepLabels[step] }}</span>
            </div>
        </header>
        <Transition name="slide">
            <div class="step" v-if="activeStep === Step.ContactDetails">
                <span class="step-label">Steg {{ stepNumbers[activeStep] }}</span>
                <h4>{{ props.strings.contacts.title }}</h4>
                <p>{{ props.strings.contacts.text }}</p>
                <div v-if="isLoggedIn">
                    <p class="loginAs summary-bg">Du är inloggad som <strong>{{ data.display_name }}</strong></p>
                    <div class="buttons">
                        <button class="wa-btn is--fill is--arrow"
                                @click="gotoStep(Step.Information)">
                            <span>{{ buttonText.next }}</span>
                            <span class="icon icon-arrow-right"></span>
                        </button>
                    </div>
                    <span class="changeMethod" @click="userLogout">Logga ut och byt användare</span>
                </div>
                <div v-else-if="!isRegistration">
                    <div class="authorization">
                        <div class="row">
                            <p class="subtitle">Ny kund</p>
                            <div class="wysiwyg-content" v-html="props.strings.contacts.registration_text"></div>
                            <button class="wa-btn is--fill is--arrow" @click="isRegistration = true">
                                <span>Skapa konto</span>
                                <span class="icon icon-arrow-right"></span>
                            </button>
                        </div>
                        <div class="row">
                            <p class="subtitle">Logga in </p>
                            <label class="input">
                                <span class="label">E-postadress*</span>
                                <input type="text" name="email" v-model.trim="data.email" required>
                                <span class="validation" v-html="validationText.email"></span>
                            </label>
                            <label class="input">
                                <span class="label">Lösenord</span>
                                <input type="password" name="password" v-model.trim="data.password"
                                       required @keyup.enter="userLogin">
                                <span class="validation" v-html="validationText.password"></span>
                            </label>
                            <p class="lostPassLink">
                                <span>Glömt ditt lösenord? </span>
                                <a :href="props.lost_password_url" class="lostPassLink">Återställ lösenord</a>
                            </p>
                            <button class="wa-btn is--fill is--arrow" @click="userLogin">
                                <span>Logga in</span>
                                <span class="icon icon-arrow-right"></span>
                            </button>
                        </div>
                    </div>
                    <p class="errorMessage"></p>
                </div>
                <div v-if="isRegistration">
                    <label class="input">
                        <span class="label">Förnamn*</span>
                        <input type="text" name="name" v-model="data.name" required>
                        <span class="validation" v-html="validationText.name"></span>
                    </label>
                    <label class="input">
                        <span class="label">Efternamn</span>
                        <input type="text" name="surname" v-model="data.surname">
                    </label>
                    <label class="input">
                        <span class="label">E-postadress*</span>
                        <input type="text" name="email" v-model.trim="data.email" required>
                        <span class="validation" v-html="validationText.email"></span>
                    </label>
                    <label class="input">
                        <span class="label">Telefonnummer</span>
                        <input type="text" name="phone" v-model="data.phone">
                    </label>
                    <div v-if="isRegistration" class="input">
                        <span class="label">Lösenord</span>
                        <p>Ditt lösenord genereras automatiskt och skickas till din e-postadress.</p>
                    </div>
                    <label class="wa-checkbox input" v-if="props.strings.contacts.required_checkbox_text">
                        <input type="checkbox" required v-model="checkbox">
                        <span class="wa-checkbox-label" v-html="props.strings.contacts.required_checkbox_text"></span>
                    </label>
                    <label class="wa-checkbox input" v-if="props.strings.contacts.checkbox_text">
                        <input type="checkbox">
                        <span class="wa-checkbox-label" v-html="props.strings.contacts.checkbox_text"></span>
                    </label>
                    <div class="buttons">
                        <button class="wa-btn is--fill is--arrow"
                                @click="gotoStep(Step.Information)" :disabled="!checkbox">
                            <span>{{ buttonText.next }}</span>
                            <span class="icon icon-arrow-right"></span>
                        </button>
                    </div>
                    <p class="errorMessage"></p>
                    <span class="changeMethod" @click="isRegistration=false">Byt inloggningssätt</span>
                </div>
            </div>
            <div class="step" v-else-if="activeStep === Step.Information">
                <span class="step-label">Steg {{ stepNumbers[activeStep] }}</span>
                <h4>{{ props.strings.information.title }}</h4>
                <p>{{ props.strings.information.text }}</p>
                <label class="input">
                    <span class="label">Stad*</span>
                    <input type="text" name="city" v-model="data.city" required placeholder="Stad" maxlength="50">
                    <span class="validation" v-html="validationText.city"></span>
                </label>
                <label class="input">
                    <span class="label">Språk*</span>
                    <input type="text" name="language" v-model="data.language" required placeholder="Språk" maxlength="50">
                    <span class="validation" v-html="validationText.language"></span>
                </label>
                <label class="input select">
                    <span class="label">Inriktning*
                        <a class="label-link changeMethod" :href="props.strings.information.orientation_link.url"
                           v-if="props.strings.information.orientation_link.url"
                           :target="props.strings.information.orientation_link.target">{{ props.strings.information.orientation_link.title }}</a>
                    </span>
                    <select name="orientation" v-model="data.orientation">
                        <option disabled value="">Välj inriktning</option>
                        <option :value="item.option" v-for="item in props.strings.information.orientation">{{ item.option }}</option>
                    </select>
                    <span class="validation" v-html="validationText.orientation"></span>
                </label>
                <label class="field textarea">
                    <span class="label">Kortfattad beskrivning*</span>
                    <textarea v-model="data.text"
                              placeholder="Här kan du kortfattat beskriva vad du behöver hjälp med för att hjälpa oss att hitta rätt advokat för dig" required></textarea>
                    <span class="validation" v-html="validationText.text"></span>
                </label>
                <div class="buttons">
                    <button class="wa-btn" @click="gotoStep(Step.ContactDetails)">{{ buttonText.back }}</button>
                    <button class="wa-btn is--fill is--arrow" @click="gotoStep(Step.Payment)">
                        <span>{{ buttonText.next }}</span>
                        <span class="icon icon-arrow-right"></span>
                    </button>
                </div>
            </div>
            <div class="step" v-else-if="activeStep === Step.Payment">
                <span class="step-label">Steg {{ stepNumbers[activeStep] }}</span>
                <h4>{{ props.strings.payment.title }}</h4>
                <p>{{ props.strings.payment.text }}</p>
                <div class="summary">
                    <div class="summary-box">
                        <p class="subtitle">Sammanställning</p>
                        <ul class="summary-list">
                            <li>
                                <span>Förnamn</span>
                                <span>{{ data.name }}</span>
                            </li>
                            <li v-if="data.surname">
                                <span>Efternamn</span>
                                <span>{{ data.surname }}</span>
                            </li>
                            <li>
                                <span>E-post</span>
                                <span>{{ data.email }}</span>
                            </li>
                            <li v-if="data.phone">
                                <span>Telefonnummer</span>
                                <span>{{ data.phone }}</span>
                            </li>
                        </ul>
                    </div>
                    <div class="summary-box">
                        <p class="subtitle">Vilken advokat söker du?</p>
                        <div>
                            <span style="display: block;"><strong>Stad:</strong> {{ data.city }}</span>
                            <span style="display: block;"><strong>Språk:</strong> {{ data.language }}</span>
                            <span style="display: block;"><strong>Inriktning:</strong> {{ data.orientation }}</span>
                            <p class="summary-text"><strong>Kortfattad beskrivning:</strong> {{ data.text }}</p>
                        </div>
                    </div>
                    <p class="subtitle">Pris</p>
                    <p class="summary-bg">
                        <span class="summary-price">{{ getFormatedPrice(data.product.price) }}</span>
                        <i>Varav 25 % moms: {{ getTaxAmount(data.product.price) }}</i>
                    </p>
                    <p class="subtitle">Typ av betalningsmetod</p>
                    <div class="radio radio-payment">
                        <label class="summary-bg">
                            <input type="radio" name="payment-type" value="stripe" checked>
                            <span class="wa-radio"></span>
                            <span class="radio-label">
                                <span>Kortbetalning via Stripe</span>
                                <img :src="props.images.stripe" alt="stripe">
                            </span>
                        </label>
                    </div>
                </div>
                <div class="buttons">
                    <button class="wa-btn" @click="gotoStep(Step.Information)">{{ buttonText.back }}</button>
                    <button class="wa-btn is--fill goToPayment" @click="createCheckoutSession">
                        <span>Betala</span>
                    </button>
                </div>
                <p class="errorMessage"></p>
            </div>
        </Transition>
    </div>
    <span class="loader" :class="{'is--active': loading}"></span>
</template>
